import { Link } from '@inertiajs/react'
import corgiLyingImg from '@/assets/home/corgi-lying.webp'
import crestImg from '@/assets/home/crest.webp'
import { useT, type T } from '@/i18n'

const columns = (t: T): { title: string; links: { label: string; href: string }[] }[] => [
  {
    title: t('About Lilu Pets'),
    links: [
      { label: t('About Us'), href: '/about' },
      { label: t('Contact Us'), href: '/about#contact' },
      { label: t('Help'), href: '/how-it-works' },
      { label: t('For owners'), href: '/how-it-works/owners' },
      { label: t('For sitters'), href: '/how-it-works/sitters' },
      { label: t('Pet Safety'), href: '/pet-safety' },
      { label: t('Lost pet? Lost mode & QR tag'), href: '/lost-pets' },
      { label: t('Holiday checklist for owners'), href: '/holiday-checklist' },
      { label: t('Free guide for new sitters'), href: '/sitter-guide' },
      { label: t('Shelter volunteers'), href: '/volunteers' },
    ],
  },
  {
    title: t('Our Services'),
    links: [
      { label: t('Pet-Boarding'), href: '/' },
      { label: t('Drop-In Visits'), href: '/' },
      { label: t('Dog-Walking'), href: '/' },
    ],
  },
  {
    title: t('More info'),
    links: [
      { label: t('Terms Of Service'), href: '/pages/terms' },
      { label: t('Privacy'), href: '/pages/privacy' },
      { label: t('Cookie Policy'), href: '/pages/cookies' },
      { label: t('Refunds & Cancellation'), href: '/pages/refunds' },
      { label: t('Community Guidelines'), href: '/pages/community-guidelines' },
      { label: t('Accessibility'), href: '/pages/accessibility' },
      { label: t('Legal Notice'), href: '/pages/statement' },
    ],
  },
]

export default function Footer() {
  const t = useT()
  const legal = t('© {year} LiLu Pets — registered trade name. All rights reserved.', { year: new Date().getFullYear() })
  const cut = legal.lastIndexOf('. ')
  const legalLines = cut > 0 ? [legal.slice(0, cut + 1), legal.slice(cut + 2)] : [legal]
  return (
    <footer style={{ position: 'relative', background: '#f1f1f1', marginTop: 'auto', overflow: 'hidden', minHeight: 320 }}>
      {/* lying corgi, bottom-right (transparent cutout — sits on the footer, no frame) */}
      <img
        src={corgiLyingImg}
        alt="Corgi"
        className="lilu-footer__corgi"
        width={969}
        height={407}
        loading="lazy"
        style={{
          position: 'absolute',
          right: 0,
          bottom: 16,
          width: 540,
          height: 'auto',
          pointerEvents: 'none',
        }}
      />

      {/* tagline crest watermark, bottom-left (exact Figma badge) */}
      <img
        src={crestImg}
        alt=""
        aria-hidden="true"
        width={600}
        height={614}
        loading="lazy"
        style={{
          position: 'absolute',
          left: 28,
          bottom: -10,
          width: 230,
          height: 'auto',
          opacity: 0.12,
          transform: 'rotate(-15deg)',
          transformOrigin: 'bottom left',
          pointerEvents: 'none',
        }}
      />

      <div className="lilu-footer__inner" style={{ maxWidth: 1320, margin: '0 auto', padding: '52px 40px 36px', position: 'relative', zIndex: 1 }}>
        <div className="lilu-footer__cols" style={{ display: 'flex', gap: 72, flexWrap: 'wrap', paddingRight: 300 }}>
          {columns(t).map((col) => (
            <div key={col.title} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span style={{ fontWeight: 500, fontSize: 14, color: 'var(--lilu-orange)' }}>{col.title}</span>
              {col.links.map((l) => (
                <Link key={l.label} href={l.href} className="lilu-link" style={{ fontSize: 14, color: 'var(--lilu-ink)' }}>
                  {l.label}
                </Link>
              ))}
            </div>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontWeight: 500, fontSize: 14, color: 'var(--lilu-orange)' }}>{t('Contact us')}</span>
            <a href="mailto:support@lilupets.com" className="lilu-link" style={{ fontSize: 14, color: 'var(--lilu-ink)' }}>support@lilupets.com</a>
            <span style={{ fontSize: 13, color: 'var(--lilu-muted)' }}>Lilu Pets · KVK 42135436</span>
            {/* BTW-ID is required to be *easily accessible* (BW 3:15d), not on
                every page: it lives on the Legal Notice and /about, both linked
                from this footer. */}
            <span style={{ fontSize: 13, color: 'var(--lilu-muted)' }}>Leiden, NL</span>
            {/* "Registered trade names" is exactly what the KVK extract says
                (Handelsnamen: LiLu Pets, LiLuPets) and is a protected status
                under the Handelsnaamwet. It is NOT a trademark: ® and
                "registered trademark" mean a BOIP/EUIPO merk, which does not
                exist yet, and claiming one is a misleading commercial practice
                in the EU. Switch to ® only once BOIP grants the mark. */}
            <span style={{ fontSize: 12, color: 'var(--lilu-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {/* Two lines on purpose: on a phone the one-line version wrapped
                  mid-sentence ("All rights / reserved."). The wording stays one
                  translation key — Gleb's exact text — split at the sentence. */}
              {legalLines.map((line, i) => <span key={i} style={{ display: 'block' }}>{line}</span>)}
            </span>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              {/* Facebook Page "LiluPets" (created 2026-09-25; numeric URL until
                  a username is claimed). */}
              <a
                href="https://www.facebook.com/profile.php?id=61594680064767"
                target="_blank"
                rel="noopener"
                aria-label="Facebook"
                title="Facebook: LiluPets"
                style={socialBox}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#5b5b56">
                  <path d="M13.5 21v-7h2.3l.4-2.8h-2.7V9.4c0-.8.2-1.4 1.4-1.4h1.4V5.5c-.3 0-1.2-.1-2.2-.1-2.2 0-3.7 1.3-3.7 3.8v2H8.1V14h2.3v7h3.1Z" />
                </svg>
              </a>
              {/* role="img": aria-label on a bare span is invalid ARIA (axe
                  aria-prohibited-attr on every page, audit 2026-10-09). */}
              <span style={socialBox} role="img" aria-label="Telegram" title="Telegram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#5b5b56" aria-hidden="true">
                  <path d="M21.9 4.3 2.9 11.6c-1.1.4-1.1 1.1-.2 1.4l4.8 1.5 1.8 5.6c.2.6.1.9.8.9.5 0 .7-.2 1-.5l2.3-2.2 4.7 3.5c.9.5 1.5.2 1.7-.8l3.1-14.5c.3-1.3-.5-1.9-1.8-1.7Z" />
                </svg>
              </span>
              {/* Telegram above stays a placeholder until a channel exists. */}
              <a
                href="https://www.instagram.com/lilupetsclub/"
                target="_blank"
                rel="noopener"
                aria-label="Instagram"
                title="Instagram: @lilupetsclub"
                style={socialBox}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5b5b56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1.2" fill="#5b5b56" stroke="none" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

const socialBox: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: 'rgba(0,0,0,0.05)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}
